import objectPath from 'object-path';
import { hasSomeValueAtPaths } from '../../../util/test-object-condition';
import {DefaultSettings, settings} from "../../../settings/settings-util";
import {AlmeState} from "../../../wireup/AlmeState";
import {Reducer, Store} from "redux";
import {AlmeUIInput} from '../../../types/input';
import {AlmeUIMessage} from '../../../types/response';
import {AlmeResponseV5} from "@alme/types/response/AlmeResponse";
import {Dictionary} from "../../../types/rendering";
import { PathCondition } from '../../../settings/settings';

interface DisableElement{
    element: HTMLElement;
    onclick: ((this: GlobalEventHandlers, ev: MouseEvent) => unknown) | null;
}

interface InputBoxProcessor{
    getReducer: () => Reducer<AlmeState>;
}

interface InputBoxRequestProcessor extends InputBoxProcessor{
    process: ((input: AlmeUIInput) => AlmeUIInput);
}

interface InputBoxResponseHandler extends InputBoxProcessor{
    process: ((message: AlmeUIMessage) => AlmeUIMessage);
}

export default class InputboxLockMutator {
    _settings: DefaultSettings;
    _store: Store<AlmeState> | null = null;
    _locked: boolean;
    _responseTimer: NodeJS.Timeout | null;
    _disabledLinks: DisableElement[];

    constructor(passedSettings) {
        this._settings = passedSettings || settings;
        this._locked = false;
        this._responseTimer = null;
        this._disabledLinks = [];
    }

    static getDefaultSettings(): DefaultSettings {
        return settings;
    }

    initialize(store: Store<AlmeState>): void {
        this._store = store;
    }

    getMutator() {
        return (element: Dictionary): Dictionary => {
            if (this.shouldSuppress() || !this._locked) {
                return element;
            }

            const attributes = Object.assign({}, element.attributes,
                {
                    disabled: true,
                    placeholder: this._settings.textConstants.pendingPlaceholder,
                });

            const className = `${element.className} ${this._settings.classNames.inputboxDisabledClassName}`;

            return Object.assign({}, element, { attributes, className });
        };
    }

    buildReducerFunction(): () => Reducer<AlmeState>{
        return function(){
            return (state: AlmeState): AlmeState => state;
        };
    }

    getRequestProcessor(): InputBoxRequestProcessor  {
        return { process: this.handleInput.bind(this), 
            getReducer: this.buildReducerFunction(),
        };
    }

    getResponseHandler(): InputBoxResponseHandler {
        return { process: this.handleResponse.bind(this), 
            getReducer: this.buildReducerFunction(),
        };
    }

    //#region Setup Methods
    handleInput(input: AlmeUIInput): AlmeUIInput {
        if (this._locked ||
            this.shouldSuppress() ||
            this.isGetHistoryRequest(input) ||
            this.isReportingEventRequest(input) ||
            this.isNotificationRequest(input) ||
            this.isExtensibleRequest(input)) {
            return input;
        }

        this._locked = true;
        this.disableLinks();

        this._responseTimer = setTimeout(() => {
            if (this.shouldSuppress()) return input;

            this._locked = false;
            this.enableLinks();
            this.resetInputBox();

        }, this.getTimeout());

        return input;
    }

    handleResponse(message: AlmeUIMessage): AlmeUIMessage  {
        if (!this._locked || !message || this.shouldSuppress()) return message;

        // CWC-609 - Leave pending=true for deferred AppCall
        if (message.response && (message.response as AlmeResponseV5).deferredAppCall) return message;

        if (this.isNotificationRequest(message) && !message.response) return message;

        this._locked = false;
        this.enableLinks();
        this.focusInputBox();

        if (this._responseTimer) {
            clearTimeout(this._responseTimer);
        }

        return message;
    }

    //#endregion



    //#region Input Box Methods

    resetInputBox(): void {
        const input = this.getInputBox();
        const origPlaceholder = this._settings.textConstants.inputboxPlaceholderText;
        const origClass = this._settings.classNames.inputboxTextAreaClassName;
        input.disabled = false;
        input.className = origClass;
        input.setAttribute('placeholder', origPlaceholder);

        if (this.isDesktop()) {
            input.focus();
        }
    }

    focusInputBox(): void {
        if (this.isDesktop()) {
            setTimeout(() => {
                const theInputBox = this.getInputBox();
                if (theInputBox) {
                    theInputBox.focus();
                }
            }, 0);
        }
    }

    getInputBox(): HTMLInputElement {
        const inputBoxClass = this._settings.classNames.inputboxTextAreaClassName;

        return document.getElementsByClassName(inputBoxClass)[0] as HTMLInputElement;
    }

    //#endregion



    //#region Link Methods

    disableLinks(): void {
        if (this._disabledLinks.length) {
            this.enableLinks();
        }

        const linkArray: HTMLElement[] = Array.from(document.querySelectorAll(`.${this._settings.classNames.displayLink} a`) );

        this._disabledLinks = linkArray.map(element=> {
            const { onclick } = element;
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            element.onclick = () => { };

            return { element, onclick };
        });
    }

    enableLinks(): void {
        this._disabledLinks.forEach(disabledLink => disabledLink.element.onclick = disabledLink.onclick);
        this._disabledLinks = [];
    }

    //#endregion



    //#region Helper Methods

    getTimeout(): number {
        return this._settings && this._settings.AlmeAPI && this._settings.AlmeAPI.ALME_API_REQUEST_TIMEOUT ?
            this._settings.AlmeAPI.ALME_API_REQUEST_TIMEOUT :
            settings.AlmeAPI.ALME_API_REQUEST_TIMEOUT;
    }

    shouldSuppress(): boolean {
        const conditions = this.getSuppressArray().map(s => ({ ...s, path: s.statePath }));
        const should = hasSomeValueAtPaths(conditions, this._store?.getState());
        return should;
    }

    getSuppressArray(): PathCondition<unknown>[] {
        if (!this._settings.InputBoxLock || !this._settings.InputBoxLock.suppressWhen) {
            return [];
        }

        return this._settings.InputBoxLock.suppressWhen;
    }

    isDesktop(): boolean {
        const deviceType = objectPath.get(this._store?.getState(), this._settings.stateLocations.DeviceType);

        if (!!deviceType && !!this._settings.mobileConstants && !!this._settings.mobileConstants.deviceTypes) {
            return deviceType === this._settings.mobileConstants.deviceTypes.desktop;
        }

        return true;
    }

    isGetHistoryRequest(input): boolean {
        return input.messageType === this._settings.messageConstants.getHistoryRequest;
    }

    isReportingEventRequest(input): boolean {
        return input.messageType === this._settings.messageConstants.reportingEventRequest;
    }

    isNotificationRequest(input): boolean {
        return input.messageType === this._settings.messageConstants.notificationType;
    }

    isExtensibleRequest(input): boolean {
        return input.messageType === this._settings.messageConstants.extensibleRequest;
    }

    //#endregion
}



// WEBPACK FOOTER //
// ./node_modules/@alme/ui-web/modules/inputbox-lock-mutator/src/inputbox-lock-mutator.ts